import React from 'react'
import { Head, ModuleContainer, ProfileCard, WelcomeBackRecognition } from '../../../src/component'
import { TrackPageViewed } from '../../../src/integrations/monitoring'
import { CheckoutPage, Features } from '../../../src/model'
import {
  useCachedFeatureFlag,
  useCheckoutState,
  useShouldShowWelcomeBackRecognition,
  useShowLoaderOnly,
  useTriggerRememberMeExperimentExposure,
} from '../../../src/state'
import { DesignLanguage, Heading, Loader, useIsDesignLanguage } from '@afterpaytouch/core'
import { useCheckoutSizes } from '../../../src/utils/responsive'
import { useTranslation } from '../../utils/convergence'
import { isAfterpayInAppBrowser } from '../../utils/device'
import { useMintPersistentSessionOnLogin } from '../../flow/remember-me/hooks'
import { getLoggedInLoadingMessages } from './helper'
import {
  usePaymentMethodRouting,
  usePBIRejectionRedirect,
  usePostLoginNavigation,
  usePrefetchPostLoginRoutes,
  useStartCheckoutOnceReady,
  useSyncApplePayFlagsToStore,
  useTrackOneOfferPageRouteResolved,
} from './hooks'

export const LoggedInPage: CheckoutPage = () => {
  const { t } = useTranslation()
  const { loggedInLoadingMessageKey } = useCheckoutState()
  // AP-cohort remember-me: every fresh interactive login converges on this page (recoverable and
  // LCCC routing only divert AFTER startCheckout runs here; usePostLoginRoute returns LOGGED_IN
  // whenever checkout hasn't started). Mid-flow re-logins (session expiry / mid-recovery) skip
  // this page and deliberately defer their mint to the next fresh login.
  useMintPersistentSessionOnLogin()
  useTrackOneOfferPageRouteResolved()
  // Fire a `Triggered Experiment` exposure event once per fresh interactive login, reporting the
  // consumer's remember-me (long-live session) variant. Shares the same one-shot login signal as
  // the remember-me mint (each consumer reads it independently), and deliberately lives here rather
  // than inside the mint hook — the mint's gating excludes the control arm, while both arms pass
  // through this page.
  useTriggerRememberMeExperimentExposure()
  // Pre-warm the manual-promo flag cache so the downstream Arcade summary page can read the
  // value synchronously from Redux on first paint. `useCachedFeatureFlag` reads the LD
  // variation directly and only writes to the cache once LD has resolved the flag, so calling
  // it here during the LoggedIn loading screen safely populates the cache before
  // ArcadeSummary mounts (and never pins a default value if LD is still bootstrapping).
  useCachedFeatureFlag(Features.CHECKOUT_MANUAL_PROMO_CODE_ENTRY, false)

  const { navigatePostLogin } = usePostLoginNavigation()
  const { isEnabledForApplePayMpanFlow } = useSyncApplePayFlagsToStore()
  const requestCompleted = useStartCheckoutOnceReady()
  usePaymentMethodRouting({ requestCompleted, navigatePostLogin })
  usePrefetchPostLoginRoutes()
  usePBIRejectionRedirect()

  const { headingSize } = useCheckoutSizes()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const isAfterpayInAppSession = isAfterpayInAppBrowser()
  const showLoaderOnly = useShowLoaderOnly()
  const shouldShowWelcomeBack = useShouldShowWelcomeBackRecognition()
  const pageTitle = `${t('common:brandName')}`
  const { title: loadingTitle, description: loadingDescription } = getLoggedInLoadingMessages(t, loggedInLoadingMessageKey ?? undefined)

  if (isArcadeDesignLanguage) {
    return (
      <>
        {shouldShowWelcomeBack ? (
          <WelcomeBackRecognition />
        ) : (
          <Loader alignCenter withHeader text={loggedInLoadingMessageKey ? loadingDescription : undefined} />
        )}
        <TrackPageViewed />
      </>
    )
  }

  return (
    <>
      <Head title={pageTitle} />
      {shouldShowWelcomeBack ? (
        <WelcomeBackRecognition />
      ) : isAfterpayInAppSession ? (
        <Loader alignCenter withHeader={!showLoaderOnly} />
      ) : (
        <>
          {loggedInLoadingMessageKey && <Heading size={headingSize}>{loadingTitle}</Heading>}
          <ModuleContainer padding={{ vertical: 'Large', horizontal: 'XLarge' }}>
            <ProfileCard />
            <Loader text={loadingDescription} />
          </ModuleContainer>
        </>
      )}
      <TrackPageViewed eventProperties={{ isEnabledForApplePayMpanFlow, loggedInLoadingMessageKey }} />
    </>
  )
}
